import React from "react";
import { ScrollArea } from "src/components/ui/ScrollArea";
import { Card, CardContent } from "src/components/ui/Card";

const TermsAndConditionsPage = () => {
    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <Card className="bg-white shadow-sm">
                    <CardContent className="p-6">
                        <div className="space-y-6">
                            {/* Header */}
                            <div className="text-center border-b border-gray-200 pb-6">
                                <h1 className="text-3xl font-bold text-gray-900">
                                    Terms and Conditions
                                </h1>
                                <p className="mt-2 text-sm text-gray-600">
                                    Last Updated: January 29, 2025
                                </p>
                            </div>

                            {/* Scrollable Area */}
                            <ScrollArea className="h-[70vh] pr-4">
                                <div className="space-y-8">
                                    {/* 1. Introduction */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            1. Introduction
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            Welcome to Wekruit ("Service," "we,"
                                            "us," or "our"). These Terms and
                                            Conditions govern your use of our
                                            mock interview and job application
                                            tracking service, including all
                                            associated features and
                                            functionalities, websites, and user
                                            interfaces.
                                        </p>
                                        <p className="text-gray-700 leading-relaxed mt-4">
                                            By accessing or using our Service,
                                            you agree to be bound by these Terms
                                            and Conditions. If you disagree with
                                            any part of these terms, you may not
                                            access the Service.
                                        </p>
                                    </section>

                                    {/* 2. Service Description */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            2. Service Description
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            Wekruit provides tools for mock
                                            interviews, job application
                                            tracking, and news aggregation
                                            related to job searching and career
                                            development. The Service requires
                                            access to users' Gmail accounts and
                                            may collect video and audio
                                            recordings for mock interview
                                            purposes.
                                        </p>
                                    </section>

                                    {/* 3. User Accounts */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            3. User Accounts
                                        </h2>
                                        <div className="space-y-4">
                                            {/* 3.1 Account Creation */}
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                    3.1 Account Creation
                                                </h3>
                                                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                                                    <li>
                                                        You must provide
                                                        accurate and complete
                                                        information when
                                                        creating an account.
                                                    </li>
                                                    <li>
                                                        You are responsible for
                                                        maintaining the security
                                                        of your account
                                                        credentials.
                                                    </li>
                                                    <li>
                                                        You must be at least 18
                                                        years old to use the
                                                        Service.
                                                    </li>
                                                </ul>
                                            </div>

                                            {/* 3.2 Account Termination */}
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                    3.2 Account Termination
                                                </h3>
                                                <p className="text-gray-700 mb-2">
                                                    We reserve the right to
                                                    suspend or terminate your
                                                    account at our discretion
                                                    if:
                                                </p>
                                                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                                                    <li>
                                                        You violate these Terms.
                                                    </li>
                                                    <li>
                                                        You misuse the Service.
                                                    </li>
                                                    <li>
                                                        We suspect fraudulent
                                                        activity.
                                                    </li>
                                                    <li>
                                                        We cease operations.
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </section>

                                    {/* 4. User Content and Data */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            4. User Content and Data
                                        </h2>
                                        <div className="space-y-4">
                                            {/* 4.1 Content Ownership */}
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                    4.1 Content Ownership
                                                </h3>
                                                <p className="text-gray-700 leading-relaxed">
                                                    You retain ownership of any
                                                    content you submit to the
                                                    Service, including video and
                                                    audio recordings, and
                                                    information from your Gmail
                                                    account.
                                                </p>
                                            </div>
                                            {/* 4.2 License Grant */}
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                    4.2 License Grant
                                                </h3>
                                                <p className="text-gray-700 leading-relaxed">
                                                    By submitting content to the
                                                    Service, you grant us a
                                                    non-exclusive, worldwide,
                                                    royalty-free license to use,
                                                    store, and process your
                                                    content solely for the
                                                    purpose of providing and
                                                    improving the Service.
                                                </p>
                                            </div>
                                        </div>
                                    </section>

                                    {/* 5. Prohibited Activities */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            5. Prohibited Activities
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            You agree not to:
                                        </p>
                                        <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-2">
                                            <li>
                                                Use the Service for any illegal
                                                purpose.
                                            </li>
                                            <li>
                                                Attempt to gain unauthorized
                                                access to the Service.
                                            </li>
                                            <li>
                                                Interfere with or disrupt the
                                                Service.
                                            </li>
                                            <li>
                                                Share account credentials with
                                                third parties.
                                            </li>
                                            <li>
                                                Upload malicious content or
                                                software.
                                            </li>
                                            <li>Impersonate others.</li>
                                        </ul>
                                    </section>

                                    {/* 6. Disclaimer of Warranties */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            6. Disclaimer of Warranties
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            THE SERVICE IS PROVIDED "AS IS"
                                            WITHOUT ANY WARRANTY OF ANY KIND. WE
                                            DO NOT GUARANTEE THAT THE SERVICE
                                            WILL BE UNINTERRUPTED, SECURE, OR
                                            ERROR-FREE.
                                        </p>
                                    </section>

                                    {/* 7. Limitation of Liability */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            7. Limitation of Liability
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            TO THE MAXIMUM EXTENT PERMITTED BY
                                            LAW, WE SHALL NOT BE LIABLE FOR ANY
                                            INDIRECT, INCIDENTAL, SPECIAL,
                                            CONSEQUENTIAL, OR PUNITIVE DAMAGES
                                            ARISING FROM YOUR USE OF THE
                                            SERVICE.
                                        </p>
                                    </section>

                                    {/* 8. Changes to Terms */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            8. Changes to Terms
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            We reserve the right to modify these
                                            Terms at any time. We will notify
                                            users of any material changes via
                                            email or through the Service.
                                        </p>
                                    </section>

                                    {/* 9. Governing Law */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            9. Governing Law
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            These Terms shall be governed by and
                                            construed in accordance with the
                                            laws of the jurisdiction in which
                                            the Service operates, without regard
                                            to its conflict of law provisions.
                                        </p>
                                    </section>

                                    {/* 10. Contact Information */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            10. Contact Information
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            For any questions about these Terms,
                                            please contact us at{" "}
                                            <a
                                                href="mailto:admin1@wekruit.com"
                                                className="text-blue-600 hover:text-blue-800">
                                                admin1@wekruit.com
                                            </a>
                                            .
                                        </p>
                                    </section>

                                    {/* 11. Severability */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            11. Severability
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            If any provision of these Terms is
                                            found to be unenforceable or
                                            invalid, that provision shall be
                                            limited or eliminated to the minimum
                                            extent necessary so that these Terms
                                            shall otherwise remain in full force
                                            and effect.
                                        </p>
                                    </section>

                                    {/* 12. Entire Agreement */}
                                    <section>
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                            12. Entire Agreement
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            These Terms constitute the entire
                                            agreement between you and Wekruit
                                            regarding the use of the Service,
                                            superseding any prior agreements
                                            between you and Wekruit.
                                        </p>
                                    </section>
                                </div>
                            </ScrollArea>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default TermsAndConditionsPage;
